import React, { useMemo } from "react";
import { ActivityIndicator, Animated, Platform, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { SustainQueueRow } from "../../../store/slices/ticketingPortalSlice";

type Props = {
  /** The primary ticket being inspected */
  primaryTicket: SustainQueueRow;
  /** All tickets in queue (used as fallback when resolvedDuplicates is not provided) */
  allTickets: SustainQueueRow[];
  /** Animation value for slide-in effect (0-1 or Animated.Value) */
  slideAnim?: Animated.Value | number;
  /** When set, the merge button for this ticket ID shows a loading spinner */
  mergingTicketId?: string | null;
  /** API-resolved duplicates — when provided, overrides local findDuplicates() */
  resolvedDuplicates?: SustainQueueRow[];
  /** True while the duplicate-check API call is in flight */
  duplicatesLoading?: boolean;
  /** Called when user clicks "View Details" */
  onViewDetails: (ticketId: string) => void;
  /** Called when user wants to merge into another ticket */
  onMerge: (targetTicketId: string) => void;
  /** Called when panel should close */
  onClose: () => void;
  isDark?: boolean;
};

/** Find potential duplicate tickets matching the same category/subcategory as the primary ticket.
 *  Time-window filtering is intentionally omitted here — the backend already computed
 *  possibleDuplicateCount using its own logic; we just mirror which tickets qualify. */
function findDuplicates(
  primary: SustainQueueRow,
  allTickets: SustainQueueRow[]
): SustainQueueRow[] {
  return allTickets.filter((t) => {
    // Exclude the primary ticket itself
    if (t.id === primary.id) return false;

    // Only consider open tickets
    if (t.status === "resolved" || t.status === "closed-merged") return false;

    // Must have same category
    if (t.category !== primary.category) return false;

    // Must have same subcategory (if both have one)
    if (primary.subcategory && t.subcategory) {
      if (t.subcategory !== primary.subcategory) return false;
    }

    return true;
  });
}

/** Full status display config for the duplicate panel cards */
const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  new:              { label: "New",              bg: "#DCE8FB", color: "#005CBC" },
  progress:         { label: "In Progress",      bg: "#E4E0FD", color: "#5C4EC9" },
  "waiting-it":     { label: "Escalated to IT",    bg: "#FFF6CC", color: "#8B6508" },
  "waiting-tmv":    { label: "Waiting on Vendor",   bg: "#FFF6CC", color: "#8B6508" },
  "waiting-vendor": { label: "Waiting on Vendor",   bg: "#FFF6CC", color: "#8B6508" },
  "submitted-ado":  { label: "Submitted to ADO", bg: "#FFE9D1", color: "#D0781D" },
  "waiting-user":   { label: "Waiting on User",  bg: "#FFF6CC", color: "#8B6508" },
  "waiting-vendor": { label: "Waiting on Vendor", bg: "#FFF6CC", color: "#8B6508" },
  resolved:         { label: "Closed",            bg: "#F3F4F6", color: "#6B7280" },
};

/** Format submission time as "Mon, 3:45 PM" */
function formatSubmitTime(iso: string): string {
  const d = new Date(iso);
  const dayName = d.toLocaleDateString("en-US", { weekday: "short" });
  const timeStr = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${dayName}, ${timeStr}`;
}

const DuplicatesPanel: React.FC<Props> = ({
  primaryTicket,
  allTickets,
  slideAnim,
  mergingTicketId,
  resolvedDuplicates,
  duplicatesLoading,
  onViewDetails,
  onMerge,
  onClose,
  isDark,
}) => {
  const localDuplicates = useMemo(
    () => findDuplicates(primaryTicket, allTickets),
    [primaryTicket, allTickets]
  );

  // Use API-resolved list when available, fall back to local computation
  const duplicates = resolvedDuplicates ?? localDuplicates;
  // Header count: show API list length when loaded, otherwise fall back to possibleDuplicateCount
  const displayCount = resolvedDuplicates
    ? resolvedDuplicates.length
    : (primaryTicket.possibleDuplicateCount ?? 0);

  const slideStyle = slideAnim
    ? Platform.OS === "web"
      ? { transform: `translateX(${typeof slideAnim === "number" ? slideAnim : 0}px)` }
      : { transform: [{ translateX: slideAnim as any }] }
    : {};

  return (
    <Animated.View style={[s.panel, isDark && { backgroundColor: "#202020", borderLeftColor: "#363636" }, slideStyle]}>
      {/* Header */}
      <View style={[s.header, isDark && { borderBottomColor: "#363636" }]}>
        <View style={s.headerContent}>
          <MaterialIcons name="content-copy" size={18} color="#C56F12" />
          <Text style={[s.headerTitle, isDark && { color: "#F2F2F2" }]}>
            Possible Duplicates ({duplicatesLoading ? "\u2026" : displayCount})
          </Text>
        </View>
        <Pressable onPress={onClose} hitSlop={8}>
          <MaterialIcons name="close" size={20} color={isDark ? "#B0B0B0" : "#6b7280"} />
        </Pressable>
      </View>

      {/* Loading state */}
      {duplicatesLoading ? (
        <View style={s.loadingState}>
          <ActivityIndicator size="small" color={isDark ? "#3D8BFF" : "#034895"} />
          <Text style={[s.loadingText, isDark && { color: "#B0B0B0" }]}>Checking for duplicates…</Text>
        </View>
      ) : displayCount === 0 ? (
        <View style={s.emptyState}>
          <MaterialIcons name="check-circle" size={32} color="#10b981" />
          <Text style={[s.emptyStateText, isDark && { color: "#F2F2F2" }]}>No duplicates detected</Text>
          <Text style={[s.emptyStateSubtext, isDark && { color: "#B0B0B0" }]}>
            This ticket doesn't match other open tickets in the same category/subcategory
            submitted within the past 24 hours.
          </Text>
        </View>
      ) : (
        <ScrollView style={s.body} showsVerticalScrollIndicator={false}>
          {duplicates.map((dup) => (
            <View key={dup.id} style={[s.duplicateItem, isDark && { backgroundColor: "#1a1a1a", borderColor: "#363636" }]}>
              {/* Top row: ID + status */}
              <View style={s.dupTopRow}>
                <View style={s.dupIdSection}>
                  <Text style={[s.dupId, isDark && { color: "#3D8BFF" }]}>{dup.id}</Text>
                  {(() => {
                    const sc = STATUS_CONFIG[dup.status] ?? { label: dup.status, bg: "#E4E0FD", color: "#5C4EC9" };
                    return (
                      <View style={[s.statusBadge, { backgroundColor: sc.bg }]}>
                        <Text style={[s.statusBadgeText, { color: sc.color }]}>{sc.label}</Text>
                      </View>
                    );
                  })()}
                </View>
              </View>

              {/* Title */}
              <Text style={[s.dupTitle, isDark && { color: "#F2F2F2" }]} numberOfLines={2}>
                {dup.title}
              </Text>

              {/* Info grid */}
              <View style={s.infoGrid}>
                <View style={s.infoItem}>
                  <Text style={[s.infoLabel, isDark && { color: "#6E6E6E" }]}>Submitter</Text>
                  <Text style={[s.infoValue, isDark && { color: "#B0B0B0" }]} numberOfLines={1}>
                    {dup.submittedBy ?? "—"}
                  </Text>
                </View>
                <View style={s.infoItem}>
                  <Text style={[s.infoLabel, isDark && { color: "#6E6E6E" }]}>Location</Text>
                  <Text style={[s.infoValue, isDark && { color: "#B0B0B0" }]} numberOfLines={1}>
                    {dup.location ?? "—"}
                  </Text>
                </View>
                <View style={s.infoItem}>
                  <Text style={[s.infoLabel, isDark && { color: "#6E6E6E" }]}>Submitted</Text>
                  <Text style={[s.infoValue, isDark && { color: "#B0B0B0" }]} numberOfLines={1}>
                    {formatSubmitTime(dup.submitted)}
                  </Text>
                </View>
                <View style={s.infoItem}>
                  <Text style={[s.infoLabel, isDark && { color: "#6E6E6E" }]}>Duplicates</Text>
                  <Text style={[s.infoValue, isDark && { color: "#B0B0B0" }]} numberOfLines={1}>
                    {dup.possibleDuplicateCount ?? 0} other ticket{(dup.possibleDuplicateCount ?? 0) !== 1 ? 's' : ''}
                  </Text>
                </View>
              </View>

              {/* Action buttons */}
              <View style={s.actionButtons}>
                <Pressable
                  style={({ pressed }) => [
                    s.btnSecondary,
                    isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" },
                    pressed && s.btnSecondaryPressed,
                    mergingTicketId === dup.id && s.btnDisabled,
                  ]}
                  onPress={() => onViewDetails(dup.id)}
                  disabled={!!mergingTicketId}
                >
                  <MaterialIcons name="visibility" size={13} color={isDark ? "#3D8BFF" : "#034895"} />
                  <Text style={[s.btnSecondaryText, isDark && { color: "#3D8BFF" }]}>View Details</Text>
                </Pressable>
                <Pressable
                  style={({ pressed }) => [
                    s.btnPrimary,
                    pressed && !mergingTicketId && s.btnPrimaryPressed,
                    mergingTicketId === dup.id && s.btnPrimaryLoading,
                    mergingTicketId && mergingTicketId !== dup.id && s.btnDisabled,
                  ]}
                  onPress={() => {
                    if (!mergingTicketId) onMerge(dup.id);
                  }}
                  disabled={!!mergingTicketId}
                >
                  <MaterialIcons
                    name={mergingTicketId === dup.id ? "hourglass-empty" : "merge-type"}
                    size={13}
                    color="#ffffff"
                  />
                  <Text style={s.btnPrimaryText}>
                    {mergingTicketId === dup.id ? "Merging…" : "Merge Into This"}
                  </Text>
                </Pressable>
              </View>
            </View>
          ))}
        </ScrollView>
      )}
    </Animated.View>
  );
};

const s = StyleSheet.create({
  panel: {
    flex: 1,
    backgroundColor: "#ffffff",
    borderLeftWidth: 1,
    borderLeftColor: "#e5e7eb",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
  },
  body: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111827",
    marginTop: 12,
  },
  emptyStateSubtext: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 8,
    textAlign: "center",
    lineHeight: 20,
  },
  duplicateItem: {
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: 12,
    marginVertical: 6,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  dupTopRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  dupIdSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dupId: {
    fontSize: 12,
    fontWeight: "700",
    color: "#034895",
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: "600",
  },
  dupTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: "#111827",
    lineHeight: 18,
    marginBottom: 8,
  },
  infoGrid: {
    gap: 6,
    marginBottom: 10,
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  infoLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: "#6b7280",
    minWidth: 70,
  },
  infoValue: {
    fontSize: 12,
    color: "#374151",
    flex: 1,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 8,
  },
  btnSecondary: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#034895",
    backgroundColor: "#ffffff",
  },
  btnSecondaryPressed: {
    opacity: 0.8,
  },
  btnSecondaryText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#034895",
  },
  btnPrimary: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: "#034895",
  },
  btnPrimaryPressed: {
    opacity: 0.85,
  },
  btnPrimaryLoading: {
    backgroundColor: "#3b6db5",
  },
  btnDisabled: {
    opacity: 0.45,
  },
  btnPrimaryText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#ffffff",
  },
  loadingState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: 32,
  },
  loadingText: {
    fontSize: 13,
    color: "#6b7280",
  },
});

export default DuplicatesPanel;
